import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import styles from './style.module.css';
import { ArrowUpIcon } from '../../../assets/icons/arrowUpIcon';
import { ShopIcon } from '../../../assets/icons/shopIcon';
import { ConvertIcon } from '../../../assets/icons/convertIcon';
import { FollowIcon } from '../../../assets/icons/followIcon';
import { StarIcon } from '../../../assets/icons/startIcon';
import { SalesIcon } from '../../../assets/icons/salesIcon';
import { 
  useGetSellerDashboardAnalyticsCardsQuery,
  useGetSellerDashboardSalesPerformanceQuery,
  useGetSellerDashboardCustomerInsightQuery,
  useGetSellerDashboardBestSellerProductsQuery,
  useGetSellerDashboardSalesPeakQuery,
  useGetSellerDashboardPurchaseDemographicsQuery,
  useGetSellerDashboardStoreRatingQuery
} from '../../../redux/services/dashboardApi';
import type { RootState } from '../../../redux/store';

interface StatCardProps {
  title: string;
  subtitle: string;
  value: string;
  Icon: React.FC;
  color: 'green' | 'yellow' | 'red' | 'blue';
  IconBackgroundColor: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  subtitle, 
  value, 
  Icon, 
  color, 
  IconBackgroundColor,
  isLoading 
}) => (
  <div className={`${styles.card} ${styles.statCard} ${styles[color]}`}>
    <div>
      <p className={styles.statTitle}>{title}</p>
      <p className={styles.statSubtitle}>{subtitle}</p>
      {isLoading ? (
        <p className={styles.statValue}>Loading...</p>
      ) : (
        <>
          <p className={styles.statValue}>{value}</p>
          <p className={styles.statPercentage}></p>
        </>
      )}
    </div>
    <div className={styles.statIconWrapper} style={{ backgroundColor: IconBackgroundColor }}>
      <Icon />
    </div>
  </div>
);

export const AnalyticsDashboardPage: React.FC = () => {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('week');
  
  // Get selected store from Redux
  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  // Build query params with store_id if not 'all'
  const queryParams = {
    period,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  };

  const { 
    data: analyticsData, 
    isLoading: isLoadingAnalytics,
    error: analyticsError 
  } = useGetSellerDashboardAnalyticsCardsQuery(queryParams);

  const {
    data: salesPerformanceData,
    isLoading: isLoadingSalesPerformance,
    error: salesPerformanceError
  } = useGetSellerDashboardSalesPerformanceQuery(queryParams);

  const {
    data: customerInsightData,
    isLoading: isLoadingCustomerInsight,
    error: customerInsightError
  } = useGetSellerDashboardCustomerInsightQuery(queryParams);

  const {
    data: bestSellerResponse,
    isLoading: isLoadingBestSeller,
    error: bestSellerError
  } = useGetSellerDashboardBestSellerProductsQuery(queryParams);

  const {
    data: salesPeakResponse,
    isLoading: isLoadingSalesPeak,
    error: salesPeakError
  } = useGetSellerDashboardSalesPeakQuery(queryParams);

  const {
    data: demographicsResponse,
    isLoading: isLoadingDemographics,
    error: demographicsError
  } = useGetSellerDashboardPurchaseDemographicsQuery(queryParams);

  const {
    data: storeRatingData,
    isLoading: isLoadingStoreRating,
    error: storeRatingError
  } = useGetSellerDashboardStoreRatingQuery(queryParams);

  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `₦${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `₦${(amount / 1000).toFixed(1)}k`;
    }
    return `₦${amount.toFixed(2)}`;
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} style={{ opacity: i < rating ? 1 : 0.3 }}>
        <StarIcon />
      </span>
    ));
  };

  const salesPerformanceOptions: ApexCharts.ApexOptions = {
    chart: { toolbar: { show: false } },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { 
      categories: salesPerformanceData?.data.map(item => item.label) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    yaxis: { labels: { formatter: (val) => val.toFixed(0) } },
    grid: { strokeDashArray: 4, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
    tooltip: {
      y: {
        formatter: (val) => `${val} orders`
      }
    }
  };
  
  const salesPerformanceSeries = [
    { 
      name: 'Orders', 
      data: salesPerformanceData?.data.map(item => item.total_orders) || [0, 0, 0, 0, 0, 0, 0]
    },
    {
      name: 'Sales Amount',
      data: salesPerformanceData?.data.map(item => item.total_amount) || [0, 0, 0, 0, 0, 0, 0]
    }
  ];

  const customerInsightOptions: ApexCharts.ApexOptions = {
    chart: { type: 'donut' },
    labels: ['New', 'Returning'],
    colors: ['#008FFB', '#D0E9FF'],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: '75%' } } },
  };
  const customerInsightSeries = [
    customerInsightData?.new_customers_percentage || 0,
    customerInsightData?.returning_customers_percentage || 0
  ];

  const salesPeakOptions: ApexCharts.ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    xaxis: { 
      categories: salesPeakResponse?.data?.map(item => item.label) || ['Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'], 
      labels: { style: { colors: '#6c757d' } } 
    },
    yaxis: { show: false },
    grid: { show: false },
    plotOptions: { bar: { columnWidth: '40%', borderRadius: 5 } },
    dataLabels: { enabled: false },
  };
  const salesPeakSeries = [{ 
    name: 'Sales', 
    data: salesPeakResponse?.data?.map(item => item.total_amount) || [0, 0, 0, 0, 0, 0, 0]
  }];

  const bestSellers = bestSellerResponse?.data || [];
  
  const countryFlags: { [key: string]: string } = {
    'Nigeria': '🇳🇬',
    'Ghana': '🇬🇭',
    'DC Congo': '🇨🇩',
    'Congo': '🇨🇬',
    'South Africa': '🇿🇦',
    'Benin Republic': '🇧🇯',
    'Kenya': '🇰🇪',
    'Tanzania': '🇹🇿',
    'Uganda': '🇺🇬',
    'Rwanda': '🇷🇼'
  };

  const demographics = demographicsResponse?.data?.map(item => ({
    name: item.label,
    value: item.value,
    flag: countryFlags[item.label] || '🌍'
  })) || [];

  const reviews = storeRatingData?.recent_reviews || [];

  return (
    <div className={styles.dashboardContainer}>
      <header className={styles.header}>
        <select 
          title='Period Select' 
          className={styles.timeSelect}
          value={period}
          onChange={(e) => setPeriod(e.target.value as 'week' | 'month' | 'year')}
        >
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
      </header>

      {analyticsError && (
        <div style={{ padding: '16px', backgroundColor: '#fee', color: '#c00', borderRadius: '8px', marginBottom: '24px' }}>
          Failed to load analytics data. Please try again.
        </div>
      )}

      <div className={styles.mainGrid}>
        <StatCard
          title="Shop Visit"
          subtitle="Track shop visit"
          value={analyticsData?.store_visits?.toString() || '0'}
          Icon={ShopIcon}
          color="green"
          IconBackgroundColor="#07D834"
          isLoading={isLoadingAnalytics}
        />
        <StatCard 
          title="Sales" 
          subtitle="Track sales" 
          value={analyticsData ? formatCurrency(analyticsData.total_sales_amount) : '₦0'} 
          Icon={SalesIcon} 
          color="yellow" 
          IconBackgroundColor="#D8A604"
          isLoading={isLoadingAnalytics}
        />
        <StatCard
          title="Customer Convert"
          subtitle="Track customer growth"
          value={analyticsData?.customer_convert?.toString() || '0'}
          Icon={ConvertIcon}
          color="red"
          IconBackgroundColor="#D30759"
          isLoading={isLoadingAnalytics}
        />
        <StatCard
          title="Shop Follow"
          subtitle="Track shop follow"
          value={analyticsData?.shop_follows?.toString() || '0'}
          Icon={FollowIcon}
          color="blue"
          IconBackgroundColor="#066AEB"
          isLoading={isLoadingAnalytics}
        />

        <div className={`${styles.card} ${styles.salesPerformance}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3>Sales Performance</h3>
              {salesPerformanceData && (
                <p className={styles.cardSubtitle}>
                  Total Sales: {formatCurrency(salesPerformanceData.total_sales)}
                </p>
              )}
            </div>
          </div>
          {isLoadingSalesPerformance ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <p>Loading sales data...</p>
            </div>
          ) : salesPerformanceError ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#c00' }}>
              <p>Failed to load sales performance data</p>
            </div>
          ) : (
            <Chart options={salesPerformanceOptions} series={salesPerformanceSeries} type="line" height={400} />
          )}
        </div>

        <div className={styles.sideCards}>
          <div className={styles.card}>
            <div className={styles.insightHeader}>
              <h3>Customer Insight</h3>
              <div className={styles.legend}>
                <span>
                  <i style={{ backgroundColor: '#008FFB' }}></i> New {customerInsightData && `(${customerInsightData.new_customers_percentage.toFixed(1)}%)`}
                </span>
                <span>
                  <i style={{ backgroundColor: '#D0E9FF' }}></i> Returning {customerInsightData && `(${customerInsightData.returning_customers_percentage.toFixed(1)}%)`}
                </span>
              </div>
            </div>
            <p className={styles.cardSubtitle}>Track reviews and rating by customers</p>
            {isLoadingCustomerInsight ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '180px' }}>
                <p>Loading...</p>
              </div>
            ) : customerInsightError ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '180px', color: '#c00' }}>
                <p>Failed to load data</p>
              </div>
            ) : (
              <Chart options={customerInsightOptions} series={customerInsightSeries} type="donut" height={180} />
            )}
          </div>
          <div className={styles.card}>
            <h3>Order</h3>
            <p className={styles.orderValue}>
              {salesPerformanceData?.data?.reduce((sum, item) => sum + (item.total_orders ?? 0), 0) ?? 0}
            </p>
            <p className={styles.orderSubtitle}>Orders Over Time</p>
            <p className={`${styles.statPercentage} ${styles.greenText}`}></p>
          </div>
        </div>

        <div className={`${styles.card} ${styles.bestSellers}`}>
          <h3>Best Seller Products</h3>
          {isLoadingBestSeller ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <p>Loading products...</p>
            </div>
          ) : bestSellerError ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#c00' }}>
              <p>Failed to load best seller products</p>
            </div>
          ) : bestSellers.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#9fa2b4' }}>
              <p>No best seller products available</p>
            </div>
          ) : (
            <table className={styles.productTable}>
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>PRODUCT NAME</th>
                  <th>CATEGORY</th>
                  <th>STOCK SOLD</th>
                  <th>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {bestSellers.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <div className={styles.productNameCell}>
                        <img src="https://placehold.co/42x42/" alt="product" />
                        <span>{item.name}</span>
                      </div>
                    </td>
                    <td>{item.category}</td>
                    <td>{item.stock_sold}</td>
                    <td>{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={`${styles.card} ${styles.salesPeak}`}>
          <h3>Sales peak</h3>
          <p className={styles.cardSubtitle}>Track customer active days</p>
          {isLoadingSalesPeak ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '320px' }}>
              <p>Loading...</p>
            </div>
          ) : salesPeakError ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '320px', color: '#c00' }}>
              <p>Failed to load sales peak data</p>
            </div>
          ) : (
            <Chart options={salesPeakOptions} series={salesPeakSeries} type="bar" height={320} />
          )}
        </div>

        <div className={`${styles.card} ${styles.demographics}`}>
          <h3>Purchase Demographics</h3>
          <p className={styles.cardSubtitle}>Track Customer Purchase across</p>
          {isLoadingDemographics ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <p>Loading demographics...</p>
            </div>
          ) : demographicsError ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#c00' }}>
              <p>Failed to load demographics data</p>
            </div>
          ) : demographics.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#9fa2b4' }}>
              <p>No demographics data available</p>
            </div>
          ) : (
            <div className={styles.demographicsContent}>
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
                alt="World Map"
                className={styles.worldMap}
              />
              <div className={styles.countryList}>
                {demographics.map((country) => (
                  <div key={country.name} className={styles.countryItem}>
                    <span className={styles.countryFlag}>{country.flag}</span>
                    <span className={styles.countryName}>{country.name}</span>
                    <div className={styles.progressBar}>
                      <div style={{ width: `${country.value}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={`${styles.card} ${styles.storeRating}`}>
          <h3>Store Rating</h3>
          <p className={styles.cardSubtitle}>Track reviews and rating by customers</p>
          {isLoadingStoreRating ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <p>Loading ratings...</p>
            </div>
          ) : storeRatingError ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#c00' }}>
              <p>Failed to load store rating data</p>
            </div>
          ) : (
            <>
              <div className={styles.ratingSummary}>
                <span className={styles.ratingValue}>
                  {storeRatingData?.average_rating.toFixed(1) || '0.0'} <StarIcon />
                </span>
                <span className={`${styles.statPercentage} ${storeRatingData?.change_status === 'increase' ? styles.greenText : ''}`}>
                  <ArrowUpIcon /> {storeRatingData?.percentage_change.toFixed(0) || 0}% vs last 7 days
                </span>
              </div>
              {reviews.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#9fa2b4' }}>
                  <p>No reviews available</p>
                </div>
              ) : (
                <div className={styles.reviewList}>
                  {reviews.map((review, index) => (
                    <div key={index} className={styles.reviewItem}>
                      <img 
                        src={review.reviewer.photo_url || 'https://i.pravatar.cc/40'} 
                        alt="avatar" 
                      />
                      <div className={styles.reviewContent}>
                        <h4>{review.reviewer.full_name}</h4>
                        <div className={styles.stars}>
                          {renderStars(review.rating)}
                        </div>
                        <p>{review.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};